import type { PuzzleDefinition } from '@/types';
import { PathwayType } from '@/types';

// ═══════════════════════════════════════════════════════════════════════════
// Chapter 1: "Awakening" — Tier 1
// All puzzles use Clear pathways and Unison harmony only.
// Nodes: 2–4, Pathways: 1–3, Budget: minimum + 1
// ═══════════════════════════════════════════════════════════════════════════

/**
 * ch1-01: "First Strike" (Tutorial)
 * 2 nodes, 1 pathway, 1 strike
 *
 * Layout:  A(5) —— B(4)
 * Harmony: A-B unison (diff = 0 required)
 *
 * Solution: Strike A → B(ring1) +1 = 5. Both freq 5. Unison satisfied.
 * Verification: A=5, B=4+1=5. |5-5|=0 ✓
 */
export const ch1_01: PuzzleDefinition = {
  id: 'ch1-01',
  version: 1,
  tier: 1,
  metadata: {
    title: 'First Strike',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-01-15',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.5, y: 0.3, frequency: 5, locked: false },
      { id: 'B', x: 0.5, y: 0.7, frequency: 4, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 2,
    minimumStrikes: 1,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A'], optimal: true }],
};

/**
 * ch1-02: "Twin Lights"
 * 2 nodes, 1 pathway, 1 strike — strike from the other side
 *
 * Layout:  A(8) —— B(7)
 * Harmony: A-B unison
 *
 * Solution: Strike A → B(ring1) +1 = 8. Both 8. Unison ✓
 * Verification: A=8, B=7+1=8. |8-8|=0 ✓
 */
export const ch1_02: PuzzleDefinition = {
  id: 'ch1-02',
  version: 1,
  tier: 1,
  metadata: {
    title: 'Twin Lights',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-01-15',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.3, y: 0.5, frequency: 8, locked: false },
      { id: 'B', x: 0.7, y: 0.5, frequency: 7, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 2,
    minimumStrikes: 1,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A'], optimal: true }],
};

/**
 * ch1-03: "The Line"
 * 3 nodes linear, 2 pathways, 1 strike
 *
 * Layout:  A(5) —— B(4) —— C(3)
 * Harmony: A-B unison, B-C unison
 *
 * Solution: Strike A → B(ring1)+1=5, C(ring2)+2=5. All freq 5.
 * Verification: A=5, B=4+1=5, C=3+2=5. All pairs diff=0 ✓
 */
export const ch1_03: PuzzleDefinition = {
  id: 'ch1-03',
  version: 1,
  tier: 1,
  metadata: {
    title: 'The Line',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-01-15',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.5, y: 0.2, frequency: 5, locked: false },
      { id: 'B', x: 0.5, y: 0.5, frequency: 4, locked: false },
      { id: 'C', x: 0.5, y: 0.8, frequency: 3, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'unison', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 2,
    minimumStrikes: 1,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A'], optimal: true }],
};

/**
 * ch1-04: "Triangle"
 * 3 nodes triangle, 3 pathways, 1 strike
 *
 * Layout:     A(3)
 *            / \
 *        B(2) — C(2)
 * Harmony: A-B unison, A-C unison, B-C unison
 *
 * Solution: Strike A → B(ring1)+1=3, C(ring1)+1=3. All freq 3.
 * Verification: A=3, B=2+1=3, C=2+1=3. All pairs diff=0 ✓
 * Note: B-C are ring 1 from each other, but both at ring 1 from A with
 *   2 paths to each. However B and C are each reached directly from A
 *   (ring 1, single path), so no stacking.
 */
export const ch1_04: PuzzleDefinition = {
  id: 'ch1-04',
  version: 1,
  tier: 1,
  metadata: {
    title: 'Triangle',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-01-15',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.5, y: 0.2, frequency: 3, locked: false },
      { id: 'B', x: 0.2, y: 0.8, frequency: 2, locked: false },
      { id: 'C', x: 0.8, y: 0.8, frequency: 2, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'A', target: 'C', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p3', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'unison', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 2,
    minimumStrikes: 1,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A'], optimal: true }],
};

/**
 * ch1-05: "The Star"
 * 4 nodes star (hub + 3 spokes), 3 pathways, 1 strike
 *
 * Layout:      N(5)
 *              |
 *    E(5) — Hub(6) — S(5)
 * Harmony: Hub-N unison, Hub-E unison, Hub-S unison
 *
 * Solution: Strike Hub → N+1=6, E+1=6, S+1=6. All freq 6.
 * Verification: Hub=6, N=5+1=6, E=5+1=6, S=5+1=6. All pairs diff=0 ✓
 */
export const ch1_05: PuzzleDefinition = {
  id: 'ch1-05',
  version: 1,
  tier: 1,
  metadata: {
    title: 'The Star',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-01-15',
  },
  board: {
    nodes: [
      { id: 'Hub', x: 0.5, y: 0.5, frequency: 6, locked: false },
      { id: 'N', x: 0.5, y: 0.15, frequency: 5, locked: false },
      { id: 'E', x: 0.85, y: 0.5, frequency: 5, locked: false },
      { id: 'S', x: 0.5, y: 0.85, frequency: 5, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'Hub', target: 'N', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'Hub', target: 'E', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p3', source: 'Hub', target: 'S', type: PathwayType.Clear, harmonic: 'unison', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 2,
    minimumStrikes: 1,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['Hub'], optimal: true }],
};

/**
 * ch1-06: "Two Steps"
 * 3 nodes linear, 2 pathways, 2 strikes
 *
 * Layout:  A(6) —— B(6) —— C(4)
 * Harmony: A-B unison, B-C unison
 *
 * Solution:
 *   Strike A → B(r1)+1=7, C(r2)+2=6. State: A=6, B=7, C=6.
 *   Strike B → A(r1)+1=7, C(r1)+1=7.   State: A=7, B=7, C=7.
 * Verification: All freq 7. All pairs diff=0 ✓
 */
export const ch1_06: PuzzleDefinition = {
  id: 'ch1-06',
  version: 1,
  tier: 1,
  metadata: {
    title: 'Two Steps',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-01-15',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.2, y: 0.5, frequency: 6, locked: false },
      { id: 'B', x: 0.5, y: 0.5, frequency: 6, locked: false },
      { id: 'C', x: 0.8, y: 0.5, frequency: 4, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'unison', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 3,
    minimumStrikes: 2,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A', 'B'], optimal: true }],
};

/**
 * ch1-07: "The Fork"
 * 4 nodes Y-shape (hub + 3 leaves), 3 pathways, 1 strike
 *
 * Layout:     Top(3)
 *              |
 *           Hub(4)
 *            / \
 *       Left(3) Right(3)
 * Harmony: Hub-Left unison, Hub-Right unison, Hub-Top unison
 *
 * Solution: Strike Hub → Left+1=4, Right+1=4, Top+1=4. All freq 4.
 * Verification: Hub=4, Left=3+1=4, Right=3+1=4, Top=3+1=4. All pairs diff=0 ✓
 */
export const ch1_07: PuzzleDefinition = {
  id: 'ch1-07',
  version: 1,
  tier: 1,
  metadata: {
    title: 'The Fork',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-01-15',
  },
  board: {
    nodes: [
      { id: 'Hub', x: 0.5, y: 0.4, frequency: 4, locked: false },
      { id: 'Left', x: 0.15, y: 0.75, frequency: 3, locked: false },
      { id: 'Right', x: 0.85, y: 0.75, frequency: 3, locked: false },
      { id: 'Top', x: 0.5, y: 0.1, frequency: 3, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'Hub', target: 'Left', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'Hub', target: 'Right', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p3', source: 'Hub', target: 'Top', type: PathwayType.Clear, harmonic: 'unison', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 2,
    minimumStrikes: 1,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['Hub'], optimal: true }],
};

/**
 * ch1-08: "The Chain"
 * 4 nodes linear, 3 pathways, 1 strike — uses all 3 rings
 *
 * Layout:  A(10) —— B(9) —— C(8) —— D(7)
 * Harmony: A-B unison, B-C unison, C-D unison
 *
 * Solution: Strike A → B(r1)+1=10, C(r2)+2=10, D(r3)+3=10. All freq 10.
 * Verification: A=10, B=9+1=10, C=8+2=10, D=7+3=10. All pairs diff=0 ✓
 */
export const ch1_08: PuzzleDefinition = {
  id: 'ch1-08',
  version: 1,
  tier: 1,
  metadata: {
    title: 'The Chain',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-01-15',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.15, y: 0.5, frequency: 10, locked: false },
      { id: 'B', x: 0.38, y: 0.5, frequency: 9, locked: false },
      { id: 'C', x: 0.62, y: 0.5, frequency: 8, locked: false },
      { id: 'D', x: 0.85, y: 0.5, frequency: 7, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p3', source: 'C', target: 'D', type: PathwayType.Clear, harmonic: 'unison', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 2,
    minimumStrikes: 1,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A'], optimal: true }],
};

/**
 * ch1-09: "Convergence"
 * 4 nodes linear, 3 pathways, 2 strikes
 *
 * Layout:  A(2) —— B(4) —— C(4) —— D(4)
 * Harmony: A-B unison, B-C unison, C-D unison
 *
 * Designed by reverse engineering from solved state (all 6):
 *   Solved: A=6, B=6, C=6, D=6
 *   Reverse strike B: A(r1)-1=5, C(r1)-1=5, D(r2)-2=4 → (A=5,B=6,C=5,D=4)
 *   Reverse strike D: C(r1)-1=4, B(r2)-2=4, A(r3)-3=2 → (A=2,B=4,C=4,D=4)
 *
 * Solution:
 *   Strike D: C(r1)+1=5, B(r2)+2=6, A(r3)+3=5. State: A=5,B=6,C=5,D=4.
 *   Strike B: A(r1)+1=6, C(r1)+1=6, D(r2)+2=6. State: A=6,B=6,C=6,D=6.
 * Verification: All freq 6. All pairs diff=0 ✓
 */
export const ch1_09: PuzzleDefinition = {
  id: 'ch1-09',
  version: 1,
  tier: 1,
  metadata: {
    title: 'Convergence',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-01-15',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.15, y: 0.5, frequency: 2, locked: false },
      { id: 'B', x: 0.38, y: 0.5, frequency: 4, locked: false },
      { id: 'C', x: 0.62, y: 0.5, frequency: 4, locked: false },
      { id: 'D', x: 0.85, y: 0.5, frequency: 4, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p3', source: 'C', target: 'D', type: PathwayType.Clear, harmonic: 'unison', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 3,
    minimumStrikes: 2,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['D', 'B'], optimal: true }],
};

/**
 * ch1-10: "Full Circle"
 * 4 nodes linear, 3 pathways, 2 strikes
 *
 * Layout:  A(7) —— B(7) —— C(7) —— D(5)
 * Harmony: A-B unison, B-C unison, C-D unison
 *
 * Designed by reverse engineering from solved state (all 9):
 *   Solved: A=9, B=9, C=9, D=9
 *   Reverse strike C: B(r1)-1=8, D(r1)-1=8, A(r2)-2=7 → (A=7,B=8,C=9,D=8)
 *   Reverse strike A: B(r1)-1=7, C(r2)-2=7, D(r3)-3=5 → (A=7,B=7,C=7,D=5)
 *
 * Solution:
 *   Strike A: B(r1)+1=8, C(r2)+2=9, D(r3)+3=8. State: A=7,B=8,C=9,D=8.
 *   Strike C: B(r1)+1=9, D(r1)+1=9, A(r2)+2=9. State: A=9,B=9,C=9,D=9.
 * Verification: All freq 9. All pairs diff=0 ✓
 */
export const ch1_10: PuzzleDefinition = {
  id: 'ch1-10',
  version: 1,
  tier: 1,
  metadata: {
    title: 'Full Circle',
    category: 'harmonic',
    author: 'resonance-team',
    created: '2026-01-15',
  },
  board: {
    nodes: [
      { id: 'A', x: 0.15, y: 0.5, frequency: 7, locked: false },
      { id: 'B', x: 0.38, y: 0.5, frequency: 7, locked: false },
      { id: 'C', x: 0.62, y: 0.5, frequency: 7, locked: false },
      { id: 'D', x: 0.85, y: 0.5, frequency: 5, locked: false },
    ],
    pathways: [
      { id: 'p1', source: 'A', target: 'B', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p2', source: 'B', target: 'C', type: PathwayType.Clear, harmonic: 'unison', directed: false },
      { id: 'p3', source: 'C', target: 'D', type: PathwayType.Clear, harmonic: 'unison', directed: false },
    ],
  },
  constraints: {
    strikeBudget: 3,
    minimumStrikes: 2,
    maxPropagationRings: 3,
  },
  solutions: [{ strikes: ['A', 'C'], optimal: true }],
};

export const chapter1Puzzles: PuzzleDefinition[] = [
  ch1_01, ch1_02, ch1_03, ch1_04, ch1_05,
  ch1_06, ch1_07, ch1_08, ch1_09, ch1_10,
];
